import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '../../../shared/material.module';
import { AddConnectionDirectorSearchComponent } from '../add-connection-director-search/add-connection-director-search.component';
import { AddConnectionCompanySearchComponent } from '../add-connection-company-search/add-connection-company-search.component';
import { AddConnectionOpbCompanySearchComponent } from '../add-connection-opb-company-search/add-connection-opb-company-search.component';
import { DirectorFlat } from '../../../models/director';
import { CompanyDto } from '../../../models/api/companyDto';
import { OpbCompanyDto } from '../../../models/api/opbCompanyDto';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LargeShareholderService } from '../../../services/large-shareholder.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { CreateConnectionRequest } from '../../../models/api/createConnectionRequest';

export interface AddConnectionDialogData {
  largeShareholderId: string;
  year: number;
}

type EntityType = 'company' | 'opb-company' | 'director';
type DialogMode = 'selection' | 'search-connect';

@Component({
  selector: 'app-add-connection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MaterialModule,
    AddConnectionDirectorSearchComponent,
    AddConnectionCompanySearchComponent,
    AddConnectionOpbCompanySearchComponent,
    NgxSpinnerModule
  ],
  templateUrl: './add-connection-dialog.component.html',
  styleUrls: ['./add-connection-dialog.component.scss']
})
export class AddConnectionDialogComponent implements OnInit {
  @ViewChild(AddConnectionDirectorSearchComponent) directorSearchComponent!: AddConnectionDirectorSearchComponent;
  @ViewChild(AddConnectionCompanySearchComponent) companySearchComponent!: AddConnectionCompanySearchComponent;
  @ViewChild(AddConnectionOpbCompanySearchComponent) opbCompanySearchComponent!: AddConnectionOpbCompanySearchComponent;

  currentMode: DialogMode = 'selection';
  selectedEntityType: EntityType | null = null;
  selectedDirector: DirectorFlat | null = null;
  selectedCompany: CompanyDto | null = null;
  selectedOpbCompany: OpbCompanyDto | null = null;
  isFormValid: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<AddConnectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddConnectionDialogData,
    private confirmationDialogService: ConfirmationDialogService,
    private largeShareholderService: LargeShareholderService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {}

  onSelectCompany(): void {
    this.selectedEntityType = 'company';
    this.currentMode = 'search-connect';
  }

  onSelectOpbCompany(): void {
    this.selectedEntityType = 'opb-company';
    this.currentMode = 'search-connect';
  }

  onSelectDirector(): void {
    this.selectedEntityType = 'director';
    this.currentMode = 'search-connect';
  }

  onBack(): void {
    this.currentMode = 'selection';
    this.selectedEntityType = null;
    this.selectedDirector = null;
    this.selectedCompany = null;
    this.selectedOpbCompany = null;
    this.isFormValid = false;
  }

  onConfirm(): void {
    let entityName = '';
    let entityLabel = '';

    if (this.selectedEntityType === 'director' && this.selectedDirector) {
      entityName = this.selectedDirector.fullName;
      entityLabel = 'director';
    } else if (this.selectedEntityType === 'company' && this.selectedCompany) {
      entityName = this.selectedCompany.companyName;
      entityLabel = 'company';
    } else if (this.selectedEntityType === 'opb-company' && this.selectedOpbCompany) {
      entityName = this.selectedOpbCompany.name;
      entityLabel = 'OPB company';
    } else {
      return;
    }

    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Connection',
      `Are you sure you want to add a connection to ${entityLabel} "${entityName}"?`,
      'Add Connection',
      'Cancel',
      'link',
      'clear'
    ).subscribe(confirmed => {
      if (confirmed) {
        this.createConnection();
      }
    });
  }

  private createConnection(): void {
    const request: CreateConnectionRequest = {
      largeShareholderId: this.data.largeShareholderId
    };

    let entityLabel = '';

    if (this.selectedEntityType === 'director' && this.selectedDirector) {
      request.directorId = this.selectedDirector.id;
      entityLabel = 'Director';
    } else if (this.selectedEntityType === 'company' && this.selectedCompany) {
      request.companyId = this.selectedCompany.id;
      entityLabel = 'Company';
    } else if (this.selectedEntityType === 'opb-company' && this.selectedOpbCompany) {
      request.opbCompanyId = this.selectedOpbCompany.id;
      entityLabel = 'OPB Company';
    } else {
      return;
    }

    this.spinnerService.show();
    this.largeShareholderService.createConnection(request).subscribe({
      next: (connection) => {
        this.spinnerService.hide();
        this.toast.success(`${entityLabel} connection added successfully`, {
          iconTheme: {
            primary: 'var(--mat-sys-primary)',
            secondary: '#FFF',
          }
        });
        this.dialogRef.close({
          entityType: this.selectedEntityType,
          connection: connection
        });
      },
      error: (error) => {
        this.spinnerService.hide();
        // Surface the backend's message (e.g. the "already has a connection" rule) instead of a
        // generic one, so the user knows to remove the existing connection first.
        const serverMessage = error?.error?.error;
        this.toast.error(serverMessage || `Error adding ${entityLabel.toLowerCase()} connection`, {
          iconTheme: {
            primary: 'var(--mat-sys-error)',
            secondary: '#FFF',
          }
        });
        console.error(`Error adding ${entityLabel.toLowerCase()} connection:`, error);
      }
    });
  }

  onDirectorSelected(director: DirectorFlat | null): void {
    this.selectedDirector = director;
    this.isFormValid = director !== null;
  }

  onCompanySelected(company: CompanyDto | null): void {
    this.selectedCompany = company;
    this.isFormValid = company !== null;
  }

  onOpbCompanySelected(opbCompany: OpbCompanyDto | null): void {
    this.selectedOpbCompany = opbCompany;
    this.isFormValid = opbCompany !== null;
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  getDialogTitle(): string {
    switch (this.currentMode) {
      case 'selection':
        return 'Add New Connection';
      case 'search-connect':
        return this.getSearchConnectTitle();
      default:
        return 'Add New Connection';
    }
  }

  getSearchConnectTitle(): string {
    switch (this.selectedEntityType) {
      case 'company':
        return 'Search & Connect Company';
      case 'opb-company':
        return 'Search & Connect OPB Company';
      case 'director':
        return 'Search & Connect Director';
      default:
        return 'Search & Connect';
    }
  }
}
